import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Bot,
  ExternalLink,
  Loader2,
  MessageCircle,
  Send,
  X,
} from 'lucide-react';
import { useAuth } from '../lib/auth';

const greeting = {
  role: 'assistant',
  content:
    'Hi, I’m Q — ready to assist. What would you like to check?',
};

const quickPrompts = [
  'Check platform health',
  'Summarise today’s operations',
  'Review recent errors',
  'Check scheduled jobs',
  'Inspect this page',
];

const QAssistantWidget = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState([greeting]);
  const scrollRef = useRef(null);

  const authorised = ['super_admin', 'admin'].includes(user?.role);

  useEffect(() => {
    if (
      authorised
      && !sessionStorage.getItem('q_welcome_dismissed')
    ) {
      const timer = window.setTimeout(() => {
        setShowWelcome(true);
      }, 900);

      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, [authorised]);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop =
        scrollRef.current.scrollHeight;
    }
  }, [messages, sending, open]);

  if (!authorised) return null;

  const dismissWelcome = () => {
    setShowWelcome(false);
    sessionStorage.setItem('q_welcome_dismissed', 'true');
  };

  const openQ = () => {
    dismissWelcome();
    setOpen(true);
  };

  const sendMessage = async (requestedMessage) => {
    const outgoing = (
      typeof requestedMessage === 'string'
        ? requestedMessage
        : message
    ).trim();

    if (!outgoing || sending) return;

    setMessage('');
    setError('');
    setSending(true);
    setMessages((items) => [
      ...items,
      { role: 'user', content: outgoing },
    ]);

    try {
      const token = localStorage.getItem('access_token');
      const conversationId =
        localStorage.getItem('q_conversation_id');

      /*
       * Capture the current application route immediately before
       * every Q request. The backend independently reconstructs all
       * authenticated page facts.
       */
      const currentPagePath =
        window.location.pathname || '/';

      const pageContextVersion =
        'q-page-context-v2';

      const response = await fetch('/api/q/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token
            ? { Authorization: `Bearer ${token}` }
            : {}),
          'X-Q-Page-Context': pageContextVersion,
        },
        body: JSON.stringify({
          message: outgoing,
          conversation_id: conversationId || null,
          page_path: currentPagePath,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || `Q returned HTTP ${response.status}`
        );
      }

      localStorage.setItem(
        'q_conversation_id',
        data.conversation_id
      );

      setMessages((items) => [
        ...items,
        {
          role: 'assistant',
          content: data.answer,
        },
      ]);
    } catch (requestError) {
      setError(
        requestError.message || 'Q is temporarily unavailable.'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-[80]">
      {!open && showWelcome && (
        <div className="absolute bottom-16 right-0 w-80 rounded-2xl border border-violet-500/30 bg-zinc-950 p-4 text-zinc-100 shadow-2xl">
          <button
            type="button"
            aria-label="Dismiss Q welcome"
            onClick={dismissWelcome}
            className="absolute right-2 top-2 rounded-lg p-1 text-zinc-500 hover:bg-zinc-800 hover:text-white"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex gap-3 pr-5">
            <div className="h-fit rounded-xl bg-violet-500/15 p-2">
              <Bot className="h-5 w-5 text-violet-300" />
            </div>
            <button
              type="button"
              onClick={openQ}
              className="text-left text-sm leading-6 text-zinc-200"
            >
              Hi, I’m Q — ready to assist. What would you like
              to check?
            </button>
          </div>
        </div>
      )}

      {open && (
        <section className="mb-3 flex h-[min(650px,75vh)] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-violet-500/30 bg-[#09090b] text-zinc-100 shadow-2xl sm:w-[420px]">
          <header className="flex items-center justify-between border-b border-zinc-800 bg-gradient-to-r from-violet-950/80 to-cyan-950/50 px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-violet-500/20 p-2">
                <Bot className="h-5 w-5 text-violet-300" />
              </div>
              <div>
                <div className="font-semibold">Q Assistant</div>
                <div className="text-xs text-emerald-300">
                  Online · read-only · page-aware
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                type="button"
                title="Open Q Review Console"
                onClick={() => {
                  window.location.href = '/admin/q-review';
                }}
                className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-800 hover:text-white"
              >
                <ExternalLink className="h-4 w-4" />
              </button>

              <button
                type="button"
                title="Close Q"
                onClick={() => setOpen(false)}
                className="rounded-lg p-2 text-zinc-400 hover:bg-zinc-800 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </header>

          <div
            ref={scrollRef}
            className="flex-1 space-y-3 overflow-y-auto p-4"
          >
            {messages.map((item, index) => (
              <div
                key={`${item.role}-${index}`}
                className={`flex ${
                  item.role === 'user'
                    ? 'justify-end'
                    : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-6 ${
                    item.role === 'user'
                      ? 'bg-violet-600 text-white'
                      : 'border border-zinc-800 bg-zinc-900 text-zinc-200'
                  }`}
                >
                  {item.role === 'assistant' ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {item.content}
                    </ReactMarkdown>
                  ) : (
                    item.content
                  )}
                </div>
              </div>
            ))}

            {messages.length === 1 && (
              <div className="grid gap-2">
                {quickPrompts.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    onClick={() => sendMessage(prompt)}
                    className="rounded-xl border border-zinc-800 bg-zinc-950 px-3 py-2 text-left text-xs text-zinc-400 hover:border-violet-500/50 hover:text-zinc-200"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}

            {sending && (
              <div className="flex items-center gap-2 text-sm text-zinc-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                Q is thinking…
              </div>
            )}
          </div>

          {error && (
            <div className="mx-4 mb-2 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
              {error}
            </div>
          )}

          <form
            onSubmit={(event) => {
              event.preventDefault();
              sendMessage(message);
            }}
            className="border-t border-zinc-800 p-3"
          >
            <div className="flex gap-2">
              <textarea
                value={message}
                onChange={(event) =>
                  setMessage(event.target.value)
                }
                onKeyDown={(event) => {
                  if (
                    event.key === 'Enter'
                    && !event.shiftKey
                  ) {
                    event.preventDefault();
                    event.currentTarget.form?.requestSubmit();
                  }
                }}
                rows={2}
                maxLength={4000}
                placeholder="Ask Q about Rapid X…"
                className="min-h-[48px] flex-1 resize-none rounded-xl border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-violet-500"
              />

              <button
                type="submit"
                title="Send to Q"
                disabled={!message.trim() || sending}
                className="self-end rounded-xl bg-violet-600 p-3 text-white hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-2 text-center text-[10px] text-zinc-600">
              Q is read-only. Operational actions require approval.
            </div>
          </form>
        </section>
      )}

      {!open && (
        <button
          type="button"
          onClick={openQ}
          aria-label="Open Q Assistant"
          className="flex h-14 w-14 items-center justify-center rounded-full border border-violet-400/40 bg-violet-600 text-white shadow-[0_0_30px_rgba(124,58,237,0.35)] transition hover:scale-105 hover:bg-violet-500"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}
    </div>
  );
};

export default QAssistantWidget;
